import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Truck, CreditCard, Shield, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import apiInstance from '../../utils/axios';

const HeroSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch carousel images from Django backend
  useEffect(() => {
    const fetchCarousel = async () => {
      try {
        const response = await apiInstance.get('carousel/');
        // Ensure carouselData is always an array
        let carouselData = response.data;
        if (carouselData && carouselData.results) {
          carouselData = carouselData.results;
        }
        if (!Array.isArray(carouselData)) {
          carouselData = [];
        }
        
        if (carouselData.length > 0) {
          setSlides(carouselData.map((item, index) => ({
            id: item.id || index,
            image: item.image,
            title: item.title || '',
            subtitle: item.subtitle || '',
            link: item.link || '/products',
          })));
        } else {
          // Fallback slides if no carousel data
          setSlides([
            {
              id: 1,
              title: t('heroTitle'),
              subtitle: t('heroSubtitle'),
              bgColor: 'bg-gradient-to-r from-primary to-primary/80',
            },
          ]);
        }
      } catch (error) {
        console.error('Error fetching carousel:', error);
        // Fallback slides
        setSlides([
          {
            id: 1,
            title: t('heroTitle'),
            subtitle: t('heroSubtitle'),
            bgColor: 'bg-gradient-to-r from-primary to-primary/80',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchCarousel();
  }, [t]);

  // Auto-advance slides
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Quick access features with Lucide icons
  const quickFeatures = [
    { icon: Truck, title: t('fastDelivery'), desc: t('fastDeliveryDesc') },
    { icon: CreditCard, title: t('securePayment'), desc: t('securePaymentDesc') },
    { icon: Shield, title: t('qualityGuarantee'), desc: t('qualityGuaranteeDesc') },
    { icon: Zap, title: t('support24'), desc: t('support24Desc') },
  ];

  if (loading) {
    return (
      <section className="relative w-full">
        <div className="h-[200px] md:h-[300px] lg:h-[400px] bg-secondary animate-pulse" />
      </section>
    );
  }

  return (
    <section className="relative w-full">
      {/* Main Hero Slider */}
      <div className="relative h-[200px] md:h-[300px] lg:h-[400px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-transform duration-500 ease-out ${
              index === currentSlide
                ? 'translate-x-0'
                : index < currentSlide
                ? '-translate-x-full'
                : 'translate-x-full'
            }`}
          >
            <div className={`relative h-full w-full ${slide.bgColor || ''}`}>
              {slide.image ? (
                <img
                  src={slide.image}
                  alt={slide.title || 'Banner'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary/80" />
              )}
              
              {/* Overlay with text if title exists */}
              {slide.title && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="text-center px-4">
                    <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-4 text-balance">
                      {slide.title}
                    </h2>
                    {slide.subtitle && (
                      <p className="text-sm md:text-lg lg:text-xl text-white/90 mb-4 md:mb-6">
                        {slide.subtitle}
                      </p>
                    )}
                    <Button
                      size="lg"
                      onClick={() => navigate(slide.link || '/products')}
                      className="bg-card text-foreground hover:bg-card/90 font-semibold"
                    >
                      {t('shopNow')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        {slides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-card/80 hover:bg-card text-foreground flex items-center justify-center shadow-lg transition-all"
              aria-label={t('previous')}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-card/80 hover:bg-card text-foreground flex items-center justify-center shadow-lg transition-all"
              aria-label={t('next')}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {slides.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? 'w-8 bg-card'
                    : 'w-2 bg-card/50 hover:bg-card/70'
                }`}
                aria-label={`${t('goTo')} slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick Access Cards */}
      <div className="mx-auto max-w-7xl px-4 -mt-6 md:-mt-10 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {quickFeatures.map((item, index) => {
            const IconComponent = item.icon;
            return (
              <div
                key={index}
                className="flex items-center gap-3 bg-card rounded-lg p-3 md:p-4 shadow-md hover:shadow-lg transition-shadow cursor-pointer border border-border"
              >
                <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/10">
                  <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm md:text-base">{item.title}</p>
                  <p className="text-xs md:text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
